class ProductItem extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      productMediaTemplate: "[js-quick-view-media-template]",
      quickShopButton: "[js-quick-shop-button]",
      swatchImages: "[js-product-item-swatch-image]",
      swatches: "[js-swatch]",
      swatchOverflowButton: "[js-product-listing-swatch-overflow]",
    };

    this.classes = {
      active: "active",
      swatchActive: "swatch-active",
      selectedSwatch: "swatch--selected",
    };
  }

  connectedCallback() {
    this.controller = new AbortController();
    // Quick shop button present if feature enabled which means swatch overflow link opens modal
    this.quickShopButton = this.querySelector(this.selectors.quickShopButton);

    this.swatchImages = this.querySelectorAll(this.selectors.swatchImages);
    this.swatches = this.querySelectorAll(this.selectors.swatches);

    // === VARIANT-AS-CARD: keep the hover photo in sync with the selected swatch ===
    // Each swatch photo carries a "data-hover-src" attribute holding the photo that
    // follows that colour's variant photo. See documentation section 11.
    this.hoverImageLink = this.querySelector(".product-item__image--hover");
    this.hoverImage = this.hoverImageLink
      ? this.hoverImageLink.querySelector("img")
      : null;

    if (this.hoverImage) {
      this.defaultHoverImage = {
        src: this.hoverImage.getAttribute("src"),
        srcset: this.hoverImage.getAttribute("srcset"),
        sizes: this.hoverImage.getAttribute("sizes"),
        href: this.hoverImageLink.getAttribute("href"),
      };
    }
    this.swatchOverflowButton = this.querySelector(
      this.selectors.swatchOverflowButton,
    );

    // If we have swatches, add click listeners to each swatch
    this.swatches.forEach((swatch) => {
      swatch.addEventListener("click", this.handleSwatchClick.bind(this), {
        signal: this.controller.signal,
      });
    });

    // If we have swatch overflow, add a click listener
    if (this.swatchOverflowButton && this.quickShopButton) {
      this.swatchOverflowButton.addEventListener(
        "click",
        this.handleSwatchOverflowClick.bind(this),
        { signal: this.controller.signal },
      );
    }
  }

  handleSwatchClick(event) {
    const swatch = event.currentTarget;

    // If the swatch is already selected, deselect it and return
    if (swatch.classList.contains(this.classes.selectedSwatch)) {
      swatch.classList.remove(this.classes.selectedSwatch);
      this.classList.remove(this.classes.swatchActive);

      for (let i = 0; i < this.swatchImages.length; i++) {
        const swatchImage = this.swatchImages[i];

        if (swatchImage.dataset.swatchValue === swatch.dataset.value) {
          swatchImage.classList.remove(this.classes.active);
          break
        }
      }

      this.resetHoverImage();

      return
    }

    // Hide currently active swatch image
    this.swatchImages.forEach((swatchImage) => {
      swatchImage.classList.remove(this.classes.active);
    });

    // Deselect all swatches
    this.swatches.forEach((swatch) => {
      swatch.classList.remove(this.classes.selectedSwatch);
    });

    // Select the current swatch and show the associated image
    swatch.classList.add(this.classes.selectedSwatch);

    let foundSwatchImage = false;

    for (let i = 0; i < this.swatchImages.length; i++) {
      const swatchImage = this.swatchImages[i];

      if (swatchImage.dataset.swatchValue === swatch.dataset.value) {
        this.classList.add(this.classes.swatchActive);
        swatchImage.classList.add(this.classes.active);
        foundSwatchImage = true;
        this.setHoverImage(swatchImage);
        break
      }
    }

    if (!foundSwatchImage) {
      this.classList.remove(this.classes.swatchActive);
      this.resetHoverImage();
    }
  }

  // === VARIANT-AS-CARD: swap the hover photo to match the swatch that was clicked ===
  setHoverImage(swatchImage) {
    if (!this.hoverImage) return;

    const hoverSrc = swatchImage.dataset.hoverSrc;

    if (!hoverSrc) {
      this.resetHoverImage();
      return;
    }

    // A single "src" only wins if the responsive "srcset" is removed first
    this.hoverImage.removeAttribute("srcset");
    this.hoverImage.removeAttribute("sizes");
    this.hoverImage.setAttribute("src", hoverSrc);

    const swatchHref = swatchImage.getAttribute("href");

    if (swatchHref && this.hoverImageLink) {
      this.hoverImageLink.setAttribute("href", swatchHref);
    }
  }

  // === VARIANT-AS-CARD: put the card's original hover photo back ===
  resetHoverImage() {
    if (!this.hoverImage || !this.defaultHoverImage) return;

    const original = this.defaultHoverImage;

    if (original.srcset) this.hoverImage.setAttribute("srcset", original.srcset);
    if (original.sizes) this.hoverImage.setAttribute("sizes", original.sizes);
    if (original.src) this.hoverImage.setAttribute("src", original.src);

    if (original.href && this.hoverImageLink) {
      this.hoverImageLink.setAttribute("href", original.href);
    }
  }

  handleSwatchOverflowClick() {
    this.openQuickView();
  }

  disconnectedCallback() {
    this.controller.abort();
  }
}

if (!customElements.get("product-item")) {
  customElements.define("product-item", ProductItem);
}
